import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkerAndToolService } from 'src/app/shared/services/worker-and-tool.service';
import { ToolbarService } from 'src/app/shared/services/toolbar.service';
import { Subject, takeUntil } from 'rxjs';
import SignaturePad from 'signature_pad';
import { Registration } from 'src/app/shared/models/registration';
import { alert } from 'devextreme/ui/dialog';

import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { HoursOption } from 'src/app/shared/models/hours-option';
import { Worker } from 'src/app/shared/models/worker';
import { RegistrationWorkflowService, WorkflowContext } from '../../shared/services/registration-workflow.service';

@Component({
  selector: 'app-signature-tecnician',
  standalone: true,
  imports: [DxButtonModule, DxTextBoxModule],
  templateUrl: './signature-tecnician.component.html',
  styleUrl: './signature-tecnician.component.scss'
})
export class SignatureTecnicianComponent implements AfterViewInit, OnInit, OnDestroy {

  private unsubscribe = new Subject<void>();

  @ViewChild('canvas', { static: false }) canvasEl!: ElementRef<HTMLCanvasElement>;
  signaturePad!: SignaturePad;
  signatureImg: string | null = null;
  signatureNeeded = true;

  registrationId: number | null = null;
  registration!: Registration;

  option: HoursOption | undefined;
  currentWorker: Worker | undefined;
  hasControls = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workerService: WorkerAndToolService,
    private toolbar: ToolbarService,
    private workflowService: RegistrationWorkflowService
  ) {
    // config toolbar
    this.toolbar.TitleChanged("Firma Tecnico");
    this.toolbar.BackVisibleChanged(true);
    this.toolbar.ForwardVisibleChanged(true);
    //this.toolbar.NotesVisibleChanged(false);
  }

  ngOnInit(): void {
    // subscribe forward/back
    this.toolbar.forwardClickedEvent.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.onForward());
    this.toolbar.backClickedEvent.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.onBack());

    this.workerService.getHoursOption().then(val => this.option = val);
    this.workerService.getCurrentWorker().then(w => this.currentWorker = w);

    const id = this.route.snapshot.paramMap.get('id');

    this.registrationId = id ? +id : null;

    if (this.registrationId) {
      this.workerService.getTempRegistration(this.registrationId).then(reg => {
        if (reg) {
          this.registration = reg;
          this.workerService.getHasControls(reg).then(c => this.hasControls = c);

          if (reg != null && reg.signatureData != null) {
            // recupera eventuale firma già salvata
            const existingSig = (reg as Registration).signatureData!.signatureTecnician;

            if (existingSig) {
              // valore già in formato dataURL o base64 plain
              this.signatureImg = existingSig.startsWith('data:') ? existingSig : `data:image/png;base64,${existingSig}`;
              const canvas = this.canvasEl.nativeElement;
              this.signaturePad.fromDataURL(this.signatureImg!, { width: canvas.offsetWidth, height: canvas.offsetHeight });
            }
          }
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.resizeCanvas();
    this.signaturePad = new SignaturePad(this.canvasEl.nativeElement);
    // se c'era una firma caricata, ripristina
    if (this.signatureImg && this.signaturePad) {
      try {
        const canvas = this.canvasEl.nativeElement;
        this.signaturePad.fromDataURL(this.signatureImg, { width: canvas.offsetWidth, height: canvas.offsetHeight });
        this.signatureNeeded = this.signaturePad.isEmpty();
      } catch {
        // ignore if invalid
      }
    }
    window.addEventListener('resize', this.onResize);
  }


  startDrawing(_evt: Event) { /* touch handler placeholder */ }
  moved(_evt: Event) { /* touch handler placeholder */ }

  private resizeCanvas(): void {
    const canvas = this.canvasEl.nativeElement;
    const container = canvas.parentElement!;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container.clientWidth;
    const height = Math.round(width / 4.17);

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    canvas.getContext('2d')?.scale(ratio, ratio);
  }

  private onResize = (): void => {
    if (!this.signaturePad) return;
    const data = this.signaturePad.toData();
    this.resizeCanvas();
    this.signaturePad.fromData(data);
  };

  savePad(): void {
    if (!this.signaturePad) return;
    if (this.signaturePad.isEmpty()) {
      this.signatureNeeded = true;
      alert("La firma è vuota", "Attenzione");
      return;
    }
    this.signatureImg = this.signaturePad.toDataURL();
    this.signatureNeeded = false;
  }

  clearPad(): void {
    this.signaturePad?.clear();
    this.signatureImg = null;
    this.signatureNeeded = true;
  }


  async onForward(): Promise<void> {
    // cattura la firma se è stata disegnata
    if (this.signaturePad && !this.signaturePad.isEmpty()) {
      this.signatureImg = this.signaturePad.toDataURL();
      this.signatureNeeded = false;
    }

    // validazione solo se obbligatoria
    const isRequired = !!this.option?.signatureTechnicianRequired;
    if (isRequired && (!this.signatureImg || this.signatureNeeded)) {
      alert("Firma Tecnico obbligatoria", "Errore");
      return;
    }

    if (!this.registrationId) {
      alert("Registrazione non valida", "Errore");
      return;
    }

    try {
      // salva le proprietà custom sulla registrazione temporanea
      const reg = await this.workerService.getTempRegistration(this.registrationId);

      if (reg!.signatureData == null) {
        reg!.signatureData = {
          signatureCostumer: "",
          signatureCostumerName: "",
          signatureTecnician: ""
        }
      }

      (reg as Registration).signatureData!.signatureTecnician = this.signatureImg ?? "";

      this.registration.signatureData!.signatureTecnician = this.signatureImg ?? "";

      const ctx: WorkflowContext = { options: this.option!, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };

      if (this.workflowService.isFinalStep('tecnician-signing', ctx)) {
        if (this.registrationId > 0) {
          await this.workerService.editRegistration(this.registration, this.registrationId ?? 0);
        } else {
          await this.workerService.addRegistration(this.registration);
        }
        this.router.navigate(['/registrations']);
      } else {
        await this.workerService.editTempRegistration(this.registration, this.registrationId!);
        const next = this.workflowService.getNextStep('tecnician-signing', ctx);
        this.router.navigate(['/' + next, { id: this.registrationId }]);
      }

    } catch (err) {
      if (this.workerService.showSaveError(err)) return;
      console.error(err);
      alert("Errore salvataggio firma", "Errore");
    }
  }

  onBack(): void {
    const ctx: WorkflowContext = { options: this.option!, siteManagerMode: !!this.currentWorker?.siteManagerMode, hasControls: this.hasControls };
    const prev = this.workflowService.getPreviousStep('tecnician-signing', ctx);
    if (prev) {
      this.router.navigate(['/' + prev, { id: this.registrationId }]);
    } else {
      this.router.navigate(['/registration']);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}
